import type { Root } from "mdast";
import type { Plugin } from "unified";

import { $remark } from "@milkdown/kit/utils";

import { withMeta } from "../__internal__";

export interface HProperties {
  id?: string;
  className?: string | string[];
  _attrOrder?: string[];
  [key: string]: unknown;
}

/**
 * Build a Pandoc attribute string from hProperties.
 * Returns the attribute block like `{#id .class key=value}` or empty string.
 *
 * This is used by the heading toMarkdown runner to embed attributes
 * directly into the serialized MDAST during serialization.
 */
export function buildPandocAttrString(hProps: HProperties): string {
  // Filter out internal keys
  const realKeys = Object.keys(hProps).filter(
    (k) => k !== "_attrOrder"
  );
  if (realKeys.length === 0) return "";

  // Skip serialization if the only attribute is an ID (no classes, no key=value).
  // Standalone IDs are typically auto-generated by Pandoc from heading text
  // and have no semantic value. They create noise and confuse users.
  const hasClasses = hProps.className &&
    (Array.isArray(hProps.className) ? hProps.className.length > 0 : hProps.className.trim() !== '');
  const hasOtherAttrs = realKeys.some(k => k !== 'id' && k !== 'className');
  if (hProps.id && !hasClasses && !hasOtherAttrs) return "";

  const parts: string[] = [];

  // Helper to format a key=value attribute
  const formatKeyValue = (key: string, value: unknown): string => {
    if (typeof value !== "string") return "";
    return value.includes(" ") ? `${key}="${value}"` : `${key}=${value}`;
  };

  // If we have original attribute order, use it
  const attrOrder = hProps._attrOrder;
  if (attrOrder && Array.isArray(attrOrder)) {
    const usedClasses = new Set<string>();
    const usedKeys = new Set<string>();

    for (const token of attrOrder) {
      if (token === "id" && hProps.id) {
        parts.push(`#${hProps.id}`);
        usedKeys.add("id");
      } else if (token.startsWith("class:")) {
        const cls = token.slice(6);
        const classes = Array.isArray(hProps.className)
          ? hProps.className
          : typeof hProps.className === "string"
            ? hProps.className.split(" ")
            : [];
        if (classes.includes(cls)) {
          parts.push(`.${cls}`);
          usedClasses.add(cls);
        }
      } else if (token.startsWith("key:")) {
        const key = token.slice(4);
        if (hProps[key] !== undefined && typeof hProps[key] === "string") {
          parts.push(formatKeyValue(key, hProps[key]));
          usedKeys.add(key);
        }
      }
    }

    // Append any attributes not covered by attrOrder (newly added)
    if (hProps.id && !usedKeys.has("id")) {
      parts.push(`#${hProps.id}`);
    }
    const allClasses = Array.isArray(hProps.className)
      ? hProps.className
      : typeof hProps.className === "string"
        ? hProps.className.split(" ")
        : [];
    for (const cls of allClasses) {
      if (cls.trim() && !usedClasses.has(cls.trim())) {
        parts.push(`.${cls.trim()}`);
      }
    }
    for (const [key, value] of Object.entries(hProps)) {
      if (
        key !== "id" &&
        key !== "className" &&
        key !== "_attrOrder" &&
        typeof value === "string" &&
        !usedKeys.has(key)
      ) {
        parts.push(formatKeyValue(key, value));
      }
    }
  } else {
    // Fallback: original order (id, classes, then other attrs)
    if (hProps.id) {
      parts.push(`#${hProps.id}`);
    }
    if (hProps.className) {
      const classes = Array.isArray(hProps.className)
        ? hProps.className
        : hProps.className.split(" ");
      for (const cls of classes) {
        if (cls.trim()) {
          parts.push(`.${cls.trim()}`);
        }
      }
    }
    for (const [key, value] of Object.entries(hProps)) {
      if (
        key !== "id" &&
        key !== "className" &&
        key !== "_attrOrder" &&
        typeof value === "string"
      ) {
        parts.push(formatKeyValue(key, value));
      }
    }
  }

  if (parts.length === 0) return "";
  return `{${parts.join(" ")}}`;
}

/**
 * A remark plugin placeholder for heading attribute serialization.
 *
 * IMPORTANT: This plugin is intentionally a no-op transformer.
 * Previously, it was a tree transformer that ran during remark.runSync() (parsing phase),
 * which caused it to corrupt data.hProperties during parsing by re-adding attributes
 * to heading text and then deleting the properties.
 *
 * The attribute serialization is now handled directly in the heading schema's
 * toMarkdown.runner (in heading-attrs.ts), which correctly embeds attributes
 * into the MDAST during the serialization phase.
 */
const remarkHeadingAttrsStringify: Plugin<[], Root> = () => {
  // No-op transformer: attribute serialization is handled in toMarkdown.runner
  return (tree) => tree;
};

export const remarkHeadingAttrsStringifyPlugin = $remark(
  "remarkHeadingAttrsStringify",
  () => remarkHeadingAttrsStringify,
);

withMeta(remarkHeadingAttrsStringifyPlugin.plugin, {
  displayName: "Remark<remarkHeadingAttrsStringify>",
  group: "Remark",
});

withMeta(remarkHeadingAttrsStringifyPlugin.options, {
  displayName: "RemarkConfig<remarkHeadingAttrsStringify>",
  group: "Remark",
});
